import { AttributeMetaValue } from "services/KnowfullyAdminClient";
import { AttributeMetaValues } from "types/common-types";
import { ColorPickerField } from "./components/ColorPickerField";
import { ColorPreview } from "./components/ColorPreview";
import { MediaLibraryImageField } from "./components/MediaLibraryImageField";
import { MediaLibraryImagePreview } from "./components/MediaLibraryImagePreview";
import { RichTextField } from "./components/RichTextField";
import { RichTextPreview } from "./components/RichTextPreview";
import { TextFieldField } from "./components/TextFieldField";
import { TextFieldPreview } from "./components/TextFieldPreview";
import { LocationTypeDropdownField } from "./components/LocationTypeDropdownField";
import { LocationTypeDropdownPreview } from "./components/LocationTypeDropdownPreview";
import { StateDropdownField } from "./components/StateDropdownField";
import { StateDropdownPreview } from "./components/StateDropdownPreview";

export type MetaValueFieldProps = {
  metaValueName: string;
  index: number;
  attributeMetaValues?: AttributeMetaValue[];
};

export type MetaValuePreviewProps = {
  metaValueName: string;
  displayText?: string;
  attributeMetaValues?: AttributeMetaValue[];
};

export type MetaValueComponentConfig = {
  FormField: React.FC<MetaValueFieldProps>;
  Preview: React.FC<MetaValuePreviewProps>;
};

function mapKeys(
  keys: AttributeMetaValues[],
  config: MetaValueComponentConfig,
): Record<string, MetaValueComponentConfig> {
  return Object.fromEntries(keys.map((key) => [key, config]));
}

const metaValueComponents: Record<string, MetaValueComponentConfig> = {
  ...mapKeys([AttributeMetaValues.Color], {
    FormField: ColorPickerField,
    Preview: ColorPreview,
  }),
  ...mapKeys([AttributeMetaValues.MediaLibraryItem], {
    FormField: MediaLibraryImageField,
    Preview: MediaLibraryImagePreview,
  }),
  ...mapKeys([AttributeMetaValues.Description, AttributeMetaValues.Directions], {
    FormField: RichTextField,
    Preview: RichTextPreview,
  }),
  ...mapKeys([AttributeMetaValues.LocationTypeId], {
    FormField: LocationTypeDropdownField,
    Preview: LocationTypeDropdownPreview,
  }),
  ...mapKeys([AttributeMetaValues.StateId], {
    FormField: StateDropdownField,
    Preview: StateDropdownPreview,
  }),
  ...mapKeys(
    [
      AttributeMetaValues.Company,
      AttributeMetaValues.Address1,
      AttributeMetaValues.Address2,
      AttributeMetaValues.Address3,
      AttributeMetaValues.City,
      AttributeMetaValues.ZipCode,
      AttributeMetaValues.Phone,
      AttributeMetaValues.Fax,
      AttributeMetaValues.Email,
    ],
    {
      FormField: TextFieldField,
      Preview: TextFieldPreview,
    },
  ),
};

export const metaValueLabelKeys: Record<string, string> = {
  [AttributeMetaValues.Color]: "color",
  [AttributeMetaValues.MediaLibraryItem]: "image",
  [AttributeMetaValues.Description]: "descriptionLabel",
  [AttributeMetaValues.LocationTypeId]: "locationFormTypeLabel",
  [AttributeMetaValues.Company]: "locationFormCompanyLabel",
  [AttributeMetaValues.Address1]: "locationFormAddress1Label",
  [AttributeMetaValues.Address2]: "locationFormAddress2Label",
  [AttributeMetaValues.Address3]: "locationFormAddress3Label",
  [AttributeMetaValues.City]: "cityLabel",
  [AttributeMetaValues.StateId]: "locationFormStateLabel",
  [AttributeMetaValues.ZipCode]: "locationFormZipCodeLabel",
  [AttributeMetaValues.Phone]: "locationFormPhoneLabel",
  [AttributeMetaValues.Fax]: "locationFormFaxLabel",
  [AttributeMetaValues.Email]: "emailLabel",
  [AttributeMetaValues.Directions]: "locationFormDirectionsLabel",
};

export function getMetaValueComponent(
  metaValueName: string,
): MetaValueComponentConfig | undefined {
  return metaValueComponents[metaValueName];
}
